import { clsx, type ClassValue } from "clsx";

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

/** Maps our app locale codes to Intl-compatible tags. Falls back to en-US for unset/unknown. */
const INTL_LOCALE: Record<string, string> = {
  en: "en-US",
  fr: "fr-CA",
  de: "de-DE",
  am: "am-ET",
  ti: "ti-ET",
  sw: "sw-KE",
  // Latin digits: prices and dates sit inside Latin-numeral UI (serials, currency codes).
  ar: "ar-u-nu-latn",
  om: "om-ET",
  so: "so-SO",
};

function toIntlLocale(locale?: string): string {
  return (locale && INTL_LOCALE[locale]) || "en-US";
}

export function formatMoney(cents: number, currency: string = "USD", locale?: string) {
  return new Intl.NumberFormat(toIntlLocale(locale), {
    style: "currency",
    currency,
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

function withTimeZone<T extends Intl.DateTimeFormatOptions>(options: T, timeZone?: string | null): T {
  return timeZone ? { ...options, timeZone } : options;
}

export function formatDate(iso: string, locale?: string, timeZone?: string | null) {
  return new Intl.DateTimeFormat(toIntlLocale(locale), withTimeZone({
    weekday: "short",
    month: "short",
    day: "numeric",
  }, timeZone)).format(new Date(iso));
}

export function formatShortDate(iso: string, locale?: string, timeZone?: string | null) {
  return new Intl.DateTimeFormat(toIntlLocale(locale), withTimeZone({
    month: "short",
    day: "numeric",
  }, timeZone)).format(new Date(iso));
}

export function formatDateLong(iso: string, locale?: string, timeZone?: string | null) {
  return new Intl.DateTimeFormat(toIntlLocale(locale), withTimeZone({
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }, timeZone)).format(new Date(iso));
}

export function formatTime(iso: string, locale?: string, timeZone?: string | null) {
  return new Intl.DateTimeFormat(toIntlLocale(locale), withTimeZone({
    hour: "numeric",
    minute: "2-digit",
  }, timeZone)).format(new Date(iso));
}

/** Great-circle distance in km between two lat/lng points (haversine). */
export function distanceKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}
